import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Activity,
  ActivityListQuery,
  CollectionMeta,
  CreateActivityInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['activities'] as const;

export interface ActivityPage {
  items: Activity[];
  meta: CollectionMeta;
}

function toQuery(q: ActivityListQuery): Record<string, string | number | boolean | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    contactId: q.contactId,
    leadId: q.leadId,
    conversationId: q.conversationId,
    actorUserId: q.actorUserId,
    type: q.type,
    occurredBefore: q.occurredBefore,
    occurredAfter: q.occurredAfter,
    sortOrder: q.sortOrder,
  };
}

export async function listActivities(q: ActivityListQuery): Promise<ActivityPage> {
  const res = await apiClient.getCollection<Activity>('/activities', { query: toQuery(q) });
  return { items: res.data, meta: res.meta };
}
export const createActivity = (input: CreateActivityInput) =>
  apiClient.post<Activity>('/activities', input);
export const deleteActivity = (id: string) => apiClient.delete<void>(`/activities/${id}`);

export function useActivities(query: ActivityListQuery) {
  return useQuery<ActivityPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listActivities(query),
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation<Activity, ApiClientError, CreateActivityInput>({
    mutationFn: createActivity,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteActivity,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
